import { css, StyleSheet } from 'aphrodite';
import { type FC, type KeyboardEvent, useState } from 'react';

import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { isValidEmail } from './validation';

export type CcFieldProps = {
	/** Current CC email addresses. */
	value: string[];
	/** Fired with the next CC list whenever a chip is added or removed. */
	onChange: (next: string[]) => void;
	disabled?: boolean;
};

/**
 * Lightweight CC token field built from components-v2 primitives (no legacy
 * MobX `EmailCCField`). The rep types an email and commits it with Enter, comma,
 * or blur; committed emails render as removable chips. Free-form entry only —
 * no contact autocomplete (deferred; the focused-view composer sends to a single
 * known lead, and CC is for ad-hoc addresses).
 */
export const CcField: FC<CcFieldProps> = ({ value, onChange, disabled = false }) => {
	const [draft, setDraft] = useState('');

	const commit = () => {
		const candidate = draft.trim().replace(/,$/, '').trim();
		if (!candidate) {
			setDraft('');
			return;
		}
		// Silently ignore malformed or duplicate entries; keep the draft so the
		// rep can correct a typo rather than losing what they typed.
		if (!isValidEmail(candidate) || value.includes(candidate)) return;
		onChange([...value, candidate]);
		setDraft('');
	};

	const removeAt = (index: number) => {
		onChange(value.filter((_, i) => i !== index));
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ',') {
			e.preventDefault();
			commit();
			return;
		}
		// Backspace on an empty draft pops the last chip — standard token-field UX.
		if (e.key === 'Backspace' && draft.length === 0 && value.length > 0) {
			removeAt(value.length - 1);
		}
	};

	return (
		<div className={css(styles.wrap)}>
			{value.length > 0 && (
				<div className={css(styles.chips)}>
					{value.map((email, i) => (
						<span key={email} className={css(styles.chip)}>
							{email}
							<Button
								variant='ghost'
								size='icon'
								styles={[styles.chipRemove]}
								aria-label={`Remove ${email}`}
								onClick={() => removeAt(i)}
								disabled={disabled}
							>
								×
							</Button>
						</span>
					))}
				</div>
			)}
			<Input
				type='email'
				value={draft}
				onChange={e => setDraft(e.target.value)}
				onKeyDown={onKeyDown}
				onBlur={commit}
				placeholder='cc@example.com'
				disabled={disabled}
			/>
		</div>
	);
};

const styles = StyleSheet.create({
	wrap: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.xs,
	},
	chip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		padding: `2px ${THEME.spacing.xs}px 2px ${THEME.spacing.sm}px`,
		borderRadius: THEME.common.borderRadius,
		fontSize: THEME.typography.fontSizes.xs,
		background: THEME.colors.pill.gray.background,
		color: THEME.colors.text.main,
	},
	// Compact inline × — components-v2 Button (ghost/icon), with `styles` applied
	// last so they override the icon size's fixed dimensions for a chip-sized control.
	chipRemove: {
		width: 16,
		height: 16,
		minWidth: 0,
		padding: 0,
		fontSize: THEME.typography.fontSizes.sm,
		lineHeight: 1,
		color: THEME.colors.icon.gray,
	},
});
